import type { ProductSwatch } from "../products/productSearch.server";

import type { ImageGroup, Product } from "~/lib/app.types";
import { getImageGroup } from "~/shop/utils/image";

/**
 * Generates swatches for any variation attribute to enable image switching
 * All attributes will be displayed as buttons in the UI, but this enables
 * the main product image to change based on the selected attribute
 */
export const getSwatches = (
  product?: Product & { c_imageGroups?: ImageGroup[] },
): ProductSwatch[] => {
  if (
    !product?.variationAttributes ||
    product.variationAttributes.length === 0
  ) {
    return [];
  }

  const primaryAttribute = product.variationAttributes[0];

  if (!primaryAttribute?.values) {
    return [];
  }

  return (
    primaryAttribute.values.map(attributeValue => {
      const variationValues: { [key: string]: string } = {
        ...((product?.variationValues as { [key: string]: string }) || {}),
        [primaryAttribute.id]: attributeValue.value,
      };

      return {
        name: attributeValue.name || attributeValue.value,
        color: attributeValue,
        images: {
          swatch: getImageGroup(
            product?.c_imageGroups,
            "swatch",
            variationValues,
          ),
          large: getImageGroup(
            product?.c_imageGroups,
            "large",
            variationValues,
          ),
        },
        variationGroup: (() => {
          const existingGroup = product?.variationGroups?.find(group => {
            return Object.entries(group.variationValues).every(
              ([key, value]) => variationValues[key] === value,
            );
          });

          if (existingGroup) {
            return existingGroup;
          }

          const matchingVariant = product?.variants?.find(variant => {
            return Object.entries(variant.variationValues || {}).some(
              ([key, value]) => variationValues[key] === value,
            );
          });

          if (matchingVariant) {
            return {
              orderable: matchingVariant.orderable ?? false,
              price: matchingVariant.price ?? 0,
              productId: matchingVariant.productId,
              variationValues: matchingVariant.variationValues || {},
            };
          }

          return undefined;
        })(),
      };
    }) || []
  );
};
